import React, { useCallback, useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Collapse } from 'react-bootstrap';
import { ChevronDown } from 'react-bootstrap-icons';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { AuthContext } from '../../contexts/AuthContext';
import {
  DIMENSION_LABELS,
  getQuizArchetypeImageSrc,
  getTraderSegmentLabel,
  splitArchetypeBodyParagraphs,
} from '../../data/quizData';
import { getHomePageConfig } from '../../data/homePage';

function QuizRadarTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const row = item?.payload;
  const value = row?.value ?? item?.value;
  if (value === undefined || value === null) return null;
  const label = row?.dimensionFull ?? row?.dimension ?? item?.name ?? 'Theme';
  return (
    <div className="quiz-radar-tooltip">
      <div className="quiz-radar-tooltip-label">{label}</div>
      <div className="quiz-radar-tooltip-value">{value}%</div>
    </div>
  );
}

const QuizResults = ({ result, traderSegment, onRetake }) => {
  const segmentLabel = getTraderSegmentLabel(traderSegment);
  const { user } = useContext(AuthContext);
  const ctaHref = user ? '/pricing' : '/register';
  const [expandedTools, setExpandedTools] = useState(() => new Set());

  const toggleTool = useCallback((name) => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const gridStroke = '#343b4d';
  const tickFill = '#c5cdd8';
  const radarStroke = '#24BF96';
  const radarFill = '#24BF96';
  const radiusAxisTick = '#a8b4c4';

  const primary = result.primary;
  const recognitions = primary.recognitions;
  const archetypeImageSrc = getQuizArchetypeImageSrc(result.primaryKey);
  const bodyParagraphs = splitArchetypeBodyParagraphs(primary.body);

  return (
    <div className="quiz-results text-start">
      <header className="quiz-results-hero">
        {archetypeImageSrc ? (
          <img
            src={archetypeImageSrc}
            alt=""
            className="quiz-results-archetype-illustration"
            width={176}
            height={176}
            loading="lazy"
            decoding="async"
          />
        ) : null}
        <div className="quiz-results-hero-text">
          <h2 className="quiz-results-archetype-title mb-2">{primary.title}</h2>
          <p className="text-muted fst-italic mb-0 quiz-results-tagline">{primary.tagline}</p>
        </div>
      </header>

      <div className="quiz-results-top">
        <div className="quiz-results-text-col">
          <div className="quiz-results-archetype mb-4 mb-lg-0">
            <section className="quiz-results-panel quiz-results-panel-profile">
              {segmentLabel && (
                <p className="small text-muted mb-3">
                  You said you’re mostly a <span className="text-light">{segmentLabel}</span>.
                </p>
              )}
              {result.primaryKey === 'detached' ? (
                <p className="small quiz-encouraging-note mb-3">
                  None of the dysfunction themes dominate your answers—that’s why you see the Detached Professional
                  profile. It’s not a sixth “problem”; it means your pattern looks balanced relative to these themes.
                </p>
              ) : result.encouragingContext ? (
                <p className="small quiz-encouraging-note mb-3">
                  No theme runs away with the chart—the archetype is your strongest <em>relative</em> lean
                  from these answers, not a verdict on whether you’re a good trader.
                </p>
              ) : null}
              {primary.intro ? <p className="mb-3 mb-lg-0 quiz-archetype-prose">{primary.intro}</p> : null}
              {bodyParagraphs.map((para, idx) => (
                <p
                  key={idx}
                  className={`quiz-archetype-prose mb-0 ${primary.intro || idx > 0 ? 'mt-3' : ''}`}
                >
                  {para}
                </p>
              ))}

              {recognitions?.length ? (
                <div className="quiz-results-archetype-subsection">
                  <h3 className="quiz-section-heading quiz-section-heading--accent mb-2">Sound familiar?</h3>
                  <ul className="quiz-archetype-recognitions small mb-0">
                    {recognitions.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {primary.bodyClosing ? (
                <div className="quiz-results-archetype-subsection">
                  <h3 className="quiz-section-heading quiz-section-heading--callout mb-2">How PrecisionTrader helps</h3>
                  <p className="mb-0 small quiz-archetype-prose">{primary.bodyClosing}</p>
                </div>
              ) : null}

              {primary.cta ? (
                <p className="mb-0 mt-3 small quiz-archetype-prose fw-semibold text-light">{primary.cta}</p>
              ) : null}
            </section>
          </div>
        </div>

        <aside className="quiz-results-radar-aside" aria-label="Theme balance chart">
          <div className="quiz-results-panel quiz-results-panel-radar quiz-radar-wrap">
            <h3 className="quiz-section-heading quiz-section-heading--chart mb-2">
              How your answers spread across themes
            </h3>
            <div className="quiz-radar-chart-host">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart
                  cx="50%"
                  cy="50%"
                  outerRadius="58%"
                  margin={{ top: 28, right: 36, bottom: 28, left: 36 }}
                  data={result.radarRows}
                >
                  <PolarGrid stroke={gridStroke} />
                  <PolarAngleAxis
                    dataKey="dimension"
                    tick={{ fill: tickFill, fontSize: 10.5 }}
                    tickLine={false}
                  />
                  <PolarRadiusAxis
                    angle={90}
                    domain={[0, 100]}
                    tickCount={5}
                    tick={{ fill: radiusAxisTick, fontSize: 10 }}
                  />
                  <Tooltip content={QuizRadarTooltip} cursor={{ strokeDasharray: '3 3' }} />
                  <Radar
                    name="Profile"
                    dataKey="value"
                    stroke={radarStroke}
                    fill={radarFill}
                    fillOpacity={0.35}
                    dot={{ r: 4, fill: radarFill, stroke: '#e0e7ff', strokeWidth: 1 }}
                    activeDot={{ r: 7, fill: '#7fd9c4', stroke: '#eef2ff', strokeWidth: 2 }}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {result.secondaryKeys?.length > 0 ? (
              <div className="quiz-radar-other-patterns">
                <h3 className="quiz-section-heading quiz-section-heading--violet mb-2">
                  Other patterns in your answers
                </h3>
                <ul className="list-unstyled mb-0 small">
                  {result.secondaryKeys.map((k) => (
                    <li key={k} className="mb-1">
                      <span className="text-light">{DIMENSION_LABELS[k]}</span>
                      <span className="quiz-secondary-pct"> — {result.normalized[k]}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className="quiz-radar-chart-caption mb-0">
              Each dimension is one theme from your answers. Higher on a dimension means that theme came through more
              often—it&apos;s a balance snapshot, not a pass/fail score.
            </p>
          </div>
        </aside>
      </div>

      <section className="quiz-results-panel quiz-results-panel-tools quiz-tools-cta-block mb-4">
        <div className="quiz-tools-cta-intro text-center px-1 px-md-3">
          <h2 className="quiz-tools-cta-title mb-2">Put guardrails on Your Trading</h2>
          <p className="quiz-tools-cta-lead small text-muted mb-0 mx-auto">
            {Array.isArray(getHomePageConfig().hero.subheadline)
              ? getHomePageConfig().hero.subheadline.join(' ')
              : getHomePageConfig().hero.subheadline}{' '}
            Open a row below to see how each feature maps to your quiz results.
          </p>
        </div>

        <div className="quiz-tools-list-block">
          <h3 className="quiz-section-heading quiz-section-heading--tools mb-3">Tools built for this profile</h3>
          <ul className="list-unstyled mb-0 quiz-tools-expandable-list">
            {result.tools.map((t, idx) => {
              const open = expandedTools.has(t.name);
              const uid = `quiz-tool-${idx}`;
              return (
                <li key={t.name} className="quiz-tool-expandable-item">
                  <button
                    type="button"
                    className="quiz-tool-expandable-toggle"
                    onClick={() => toggleTool(t.name)}
                    aria-expanded={open}
                    aria-controls={`${uid}-desc`}
                    id={`${uid}-btn`}
                  >
                    <ChevronDown
                      className={`quiz-tool-expandable-chevron ${open ? 'is-open' : ''}`}
                      size={18}
                      aria-hidden
                    />
                    <span className="quiz-tool-expandable-name">{t.name}</span>
                  </button>
                  <Collapse in={open}>
                    <div
                      id={`${uid}-desc`}
                      role="region"
                      aria-labelledby={`${uid}-btn`}
                      className="quiz-tool-expandable-panel"
                    >
                      <p className="small text-muted mb-0 quiz-tool-expandable-desc">{t.description}</p>
                    </div>
                  </Collapse>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <div className="quiz-results-cta d-flex flex-column flex-sm-row gap-2 justify-content-center align-items-center flex-wrap text-center">
        <Button variant="outline-secondary" onClick={onRetake} className="quiz-cta-btn">
          Retake quiz
        </Button>
        <Button as={Link} to={ctaHref} variant="primary" className="quiz-cta-btn">
          Build your guardrails
        </Button>
      </div>
    </div>
  );
};

export default QuizResults;
